<template>
	<Dialog v-model:visible="visible" :closeable="true" class="custom-header custom-footer" style="width: 400px;">
		<template #header>
			<div class="ml-3 flex flex-col">
				<div class="text-sm font-light mt-2 opacity-85">{{ t('queue.addExtensionToQueueHeader') }}</div>
				<div class="text-3xl opacity-85" style="margin-top:-5px">{{ queue.name }}</div>
			</div>
		</template>

		<InputGroup class="mt-4">
			<InputText type="search" v-model="filter" :placeholder="$t('common.filter')" ref="inputFilter" size="small" @value-change="buildListOfAvailableExtensions" style="border-bottom-left-radius: 0;" />
			<InputGroupAddon>
				<Button severity="secondary" variant="text" :disabled="isSaving" size="small" @click="selectAll()" tabindex="105" iconOnly><i class="p-button-icon pi pi-plus" /></Button>
			</InputGroupAddon>
			<InputGroupAddon style="border-bottom-right-radius: 0">
				<Button severity="secondary" variant="text" :disabled="isSaving" size="small" @click="clearAll()" tabindex="106" iconOnly><i class="p-button-icon pi pi-minus" /></Button>
			</InputGroupAddon>
		</InputGroup>

		<div style="height:380px;overflow:scroll;border-left: 1px solid var(--p-form-field-border-color);border-right: 1px solid var(--p-form-field-border-color);border-bottom: 1px solid var(--p-form-field-border-color);border-radius: 0px 0px 5px 5px;">
			<div @click="toggle(item)" :class="{'selected':selected.includes(item._id)}" class="flex justify-between items-center item" v-for="item in extensionsNotInQueue" :key="item._id">
				<div class="title">{{ item.name }}</div>
				<Badge severity="primary" size="small">{{ item.number }}</Badge>
			</div>
			<div disabled v-if="extensionsNotInQueue.length === 0" style="text-align:center;margin:10px;">{{ $t('common.noneAvailable') }}</div>
		</div>

		<div class="p-1"></div>

		<div class="w-1/4">{{ t('queue.priority') }}</div>
		<Select v-model="priority" :options="priorities" size="small" option-label="label" option-value="value" />

		<template #footer>
			<Button severity="secondary" size="small" @click="close()" :disabled="isSaving" tabindex="110" class="w-36"><span>{{ t('common.cancel') }}</span></Button>
			<Button severity="primary" size="small" :disabled="selected.length === 0 || isSaving" @click="save()" tabindex="120" class="w-36"><span>{{ t('common.add') }}</span></Button>
		</template>
	</Dialog>
</template>

<script setup lang="ts">
import globalEvents from '@/utilities/GlobalEvents'
import { useEmployeesStore } from '@/stores/Employees'
import { useApiStore } from '@/stores/Api'
import { useI18n } from 'vue-i18n'
import type { PbxAppQueuesAllItem } from '@/TcxApi/PbxApp'
import { ref } from 'vue'
import { dynamicSort } from '@/utilities/Tools'
import type { ErrorResponseDetail } from '@/TcxApi/ErrorResponse'

const employees = useEmployeesStore()
const api = useApiStore()
const { t } = useI18n()

interface Props {
	queue: PbxAppQueuesAllItem
}

interface Member {
	name: string
	number?: string
	_id: string
}

const props = defineProps<Props>()
const visible = ref(false)
const priority = ref(1)
const isSaving = ref(false)
const selected = ref<string[]>([])
const extensionsNotInQueue = ref<Member[]>([])
const filter = ref('')
const priorities = ref<{ value: number, label: string }[]>([])
const inputFilter = ref<HTMLInputElement | null>(null)

function buildListOfAvailableExtensions() {
	if (!props.queue || !employees.employees) { return [] }

	extensionsNotInQueue.value = []
	const members = props.queue.members.map(member => member.extension).filter(member => member)

	for (const employee of employees.employees) {
		for (const extension of employee.extensions) {
			if (extension.primary) {
				if (!members.includes(extension._id)) {
					if (filter.value) {
						if (employee.name.toLowerCase().includes(filter.value.toLowerCase()) ||
							(employee.department && employee.department.toLowerCase().includes(filter.value.toLowerCase())) ||
							(employee.title && employee.title.toLowerCase().includes(filter.value.toLowerCase())) ||
							(employee.emailAddress && employee.emailAddress.toLowerCase().includes(filter.value.toLowerCase())) ||
							(extension.number && extension.number.includes(filter.value)) ||
							(extension.direct && extension.direct.includes(filter.value)) ||
							(Array.isArray(extension.mobile) && extension.mobile.join(' ').includes(filter.value))
						) {
							extensionsNotInQueue.value.push({ name: employee.name, number: extension.number, _id: extension._id })
						}
					} else {
						extensionsNotInQueue.value.push({ name: employee.name, number: extension.number, _id: extension._id })
					}
				}
			}
		}
	}

	extensionsNotInQueue.value.sort(dynamicSort('name'))
}

function show() {
	selected.value = []
	priority.value = 1
	filter.value = ''
	buildListOfAvailableExtensions()
	visible.value = true
	setTimeout(() => {
		inputFilter.value?.focus()
	}, 500)
}

function close() {
	visible.value = false
}

function save() {
	if (selected.value.length === 0) { return }
	isSaving.value = true
	saveMember()
}

async function saveMember() {
	const id = selected.value.shift()

	try {
		await api.post(`/pbx/queue/${props.queue._id}/member`, { extension: id, priority: priority.value })
		if (selected.value.length === 0) {
			isSaving.value = false
			close()
		} else {
			saveMember()
		}
	}
	catch (err) {
		isSaving.value = false
		const item = extensionsNotInQueue.value.find(member => member._id == id)
		const name = item ? item.name : 'N/A'

		switch ((err as ErrorResponseDetail).status) {
			case 403:
				if ((err as ErrorResponseDetail).code === 'dynamic') { showError(t('queue.queueDoesNotAllowDynamicMembers')) }
				else if ((err as ErrorResponseDetail).code === 'extension') { showError(t('queue.memberMayNotJoinQueue').replace('%%name%%', name)) }
				break
			case 404:
				showError(t('queue.memberToJoinNotFound').replace('%%name%%', name))
				break
			case 409:
				if ((err as ErrorResponseDetail).code === 'member') { close() }
				break
			default:
				close()
				showError(`${t('common.genericErrorHeader')} - code ${(err as ErrorResponseDetail).status}: ${(err as ErrorResponseDetail).message}`)
				break
		}
	}
}

function showError(message: string, header?: string) {
	globalEvents.emit('CommonErrorModal', { header: header ?? t('queue.addMemberToQueueErrorHeader'), message: message })
}

function toggle(item: Member) {
	const index = selected.value.indexOf(item._id)
	if (index != -1) {
		selected.value.splice(index, 1)
	} else {
		selected.value.push(item._id)
	}
}

function selectAll() {
	selected.value = []

	for (const extension of extensionsNotInQueue.value) {
		selected.value.push(extension._id)
	}
}

function clearAll() {
	selected.value = []
}

globalEvents.on('Auth:LoggedOut', close)
globalEvents.on('Realtime:Disconnected', close)

for (let index = 1; index <= 10; index++) {
	priorities.value.push({ value: index, label: `${index}` })
}

defineExpose({ show })
</script>

<style scoped>
	.title {
		font-size: 14px;
		color: var(--p-text-color);
	}
	.item {
		padding: 4px 10px;
		cursor: pointer;
	}
	.item:not(:last-child) {
		border-bottom: 1px solid var(--p-form-field-border-color);
	}
	.item:nth-of-type(odd) {
		background-color: #00000010;
	}
	.item.selected {
		background-color: var(--p-primary-color);
	}
	.item.selected > .title {
		color: var(--p-primary-contrast-color);
	}
</style>
